/**
 * Informe del Recorregut de Vigilància — funciones PURAS (2026-07-27).
 *
 * Aquí vive todo lo que no necesita red: resolución del preset activo,
 * construcción de las filas de la tabla de tramos y el agregado por causa de
 * pausa. Separado de `fetchSurveillanceReportData.ts` (que orquesta Apollo)
 * para poder testearlo sin navegador ni mocks.
 *
 * REGLA: la tabla de tramos del informe usa LOS MISMOS helpers que la tabla
 * de pantalla (`SegmentList`) — orden cronológico por `startDate`, Nº 1..N,
 * `HH:mm`, `Xh Ym`, `formatSegmentKm`. Si la pantalla cambia de formato, el
 * informe cambia con ella; nunca dos criterios de formateo distintos para el
 * mismo dato.
 */

import { format } from 'date-fns';
import { segmentMeters, type RoadStretch } from '../roadStretches';
import type { PkMeshVertex } from '../pkFromMesh';

import {
  SurveillanceReportOptions,
  SurveillanceSegmentRow,
  SurveillanceCauseRow,
  SR_REPORT_PRESET_SUMMARY,
  SR_REPORT_PRESET_FULL,
} from './surveillanceTypes';
import {
  formatPK,
  formatSegmentKm,
  formatSegmentDuration,
  directionNameKey,
  stripLeadingWord,
  laneName,
} from '../surveillanceRoute';
import { liveLinkedBasicActionOrder } from '../segmentLinkedBasicAction';

/**
 * Traductor mínimo — `i18n.t` real en producción, un stub en los tests.
 * `values` rellena los `{{…}}` del texto («De la OT · {{roid}}», 30-oct).
 */
export type ReportTranslate = (
  key: string,
  fallback?: string,
  values?: Record<string, string>,
) => string;

const sameOptions = (
  a: SurveillanceReportOptions,
  b: SurveillanceReportOptions,
): boolean =>
  a.segmentsTable === b.segmentsTable &&
  a.segmentExtraColumns === b.segmentExtraColumns &&
  a.causeSummary === b.causeSummary &&
  a.children === b.children &&
  // Cross-link 3 (2026-09-22): las dos casillas nuevas también cuentan.
  a.segmentPhotos === b.segmentPhotos &&
  a.linkedOrders === b.linkedOrders;

/**
 * Preset que corresponde a la selección actual, o `null` si el usuario tocó
 * alguna casilla y ya no coincide con ninguno (el modal deja de resaltar
 * ambos botones).
 */
export const resolveActivePreset = (
  options: SurveillanceReportOptions,
): 'summary' | 'full' | null => {
  if (sameOptions(options, SR_REPORT_PRESET_SUMMARY)) return 'summary';
  if (sameOptions(options, SR_REPORT_PRESET_FULL)) return 'full';
  return null;
};

/**
 * Shape mínima de tramo que necesita el informe. Se declara aquí (en vez de
 * importar `RouteSegment`) porque los campos `linked*` solo llegan con el
 * fragment DETALLE y el resto de campos son opcionales en tramos legacy.
 */
type ReportSegmentInput = {
  id?: string;
  indexExpedition?: number | null;
  indexType?: number | null;
  /** Multi-carretera (2026-08-15): carretera del tramo; nula = la primera. */
  roadId?: string | null;
  direction?: string | null;
  /** @deprecated El informe NO lo usa — ver el comentario en `directionLabel`. */
  directionName?: string | null;
  lane?: string | null;
  pkInitKm?: number | null;
  pkInitMeter?: number | null;
  pkEndKm?: number | null;
  pkEndMeter?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  pauseCause?: string | null;
  pauseCauseDetail?: string | null;
  linkedBasicActionOrder?: {
    id?: string | null;
    roid?: string | null;
    state?: string | null;
  } | null;
  linkedExternalOrder?: { id?: string | null; roid?: string | null } | null;
  /** Trabajos que atendieron el aviso (consulta aparte; spec 2026-10-27). */
  linkedExternalWorks?: { roid?: string | null }[] | null;
  /** Trozos por carretera: la distancia es su SUMA, no la resta de extremos. */
  roadStretches?: RoadStretch[] | null;
};

const EMPTY_CELL = '-';

/**
 * ROID canónico del tramo; si no hay índices, cae al id (igual que antes).
 * El sufijo es el de la clasificación, el mismo que el del recorrido (el tramo
 * la hereda al crearse y al transferirse). Sin él, el `.01` de siempre
 * (plan 2026-10-08 D6: el real suele ser `.02`).
 */
const segmentRoid = (
  segment: ReportSegmentInput,
  classificationSuffix = '01',
): string =>
  typeof segment.indexExpedition === 'number'
    ? `Tr.${String(segment.indexExpedition).padStart(7, '0')}.${String(
        segment.indexType ?? 0,
      ).padStart(4, '0')}.${classificationSuffix}`
    : (segment.id ?? '');

/**
 * Filas de la tabla de tramos del informe.
 *
 * Criterio IDÉNTICO al de `SegmentList`: orden cronológico real por
 * `startDate` (NO por `indexType` — cada sentido reinicia su numeración y el
 * PDF acababa desordenado), Nº = posición 1..N, horas `HH:mm`, duración
 * `Xh Ym`, km con un decimal. El tramo activo (sin `endDate`) muestra guiones
 * y no aporta a los agregados.
 */
export const buildSegmentRows = (
  segments: ReportSegmentInput[],
  t: ReportTranslate,
  /**
   * Nombres de las carreteras del recorrido por id (multi-carretera
   * 2026-08-15). Con MÁS DE UNA, cada fila dice la suya: un PK a secas no
   * identifica un sitio cuando una troncal y su ramal comparten kilometraje
   * sobre trazados que se separan kilómetros. Con una sola no se pinta — sería
   * una columna con el mismo valor en todas las filas.
   */
  roadNamesById?: Record<string, string>,
  /**
   * Mallas por carretera (kilómetro largo, 2026-08-26). El informe usa el MISMO
   * helper que la pantalla, así que si la tabla mide el asfalto el PDF también:
   * dos criterios distintos para el mismo dato es justo lo que esta regla
   * prohíbe. Sin mallas, el número sale como siempre.
   */
  meshesByRoadId?: Record<string, PkMeshVertex[]> | null,
  /** Sufijo de clasificación del ROID del recorrido (`Su.….….02` → `02`). */
  classificationSuffix?: string,
): SurveillanceSegmentRow[] => {
  const roadIdsOfRoute = Object.keys(roadNamesById ?? {});
  const showRoad = roadIdsOfRoute.length > 1;
  const firstRoadId = roadIdsOfRoute[0];
  const directionHeaderWord = t('forms:workOrder.direction');

  return [...(segments ?? [])]
    .sort(
      (a, b) =>
        new Date(a.startDate ?? 0).getTime() -
        new Date(b.startDate ?? 0).getTime(),
    )
    .map((segment, i) => {
      const closed = Boolean(segment.endDate);
      const hasPkEnd = segment.pkEndKm != null && segment.pkEndMeter != null;
      // El sentido se traduce SIEMPRE desde el enum `direction`. NO se usa
      // `directionName`, que es texto congelado en BD el día que se creó el
      // tramo, en el idioma que tuviera la app entonces: medido en beta,
      // 118 de 147 tramos lo tienen, en TRES idiomas y seis formatos
      // («Sentido ascendente», «Ascendent», «ascendente», y hasta un `top`
      // crudo). Peor aún: en 2 tramos ese texto CONTRADICE al enum
      // (`direction: top` con texto «Sentido descendente») — o sea, el
      // informe imprimía un dato falso. El enum es la fuente de verdad.
      const directionLabel = t(
        directionNameKey(segment.direction) ??
          'pages:surveillance.directionUnspecified',
      );
      // Trabajos que atendieron el aviso. Sin OT no hay aviso vinculado: no se
      // pintan trabajos sueltos.
      const avisoWorkRoids = segment.linkedExternalOrder?.roid
        ? (segment.linkedExternalWorks ?? [])
            .map((work) => work.roid)
            .filter((roid): roid is string => Boolean(roid))
        : [];

      return {
        index: i + 1,
        roid: segmentRoid(segment, classificationSuffix),
        // Un tramo sin carretera propia es de la PRIMERA del recorrido — la que
        // tenía implícita antes de que existiera este campo.
        // Un tramo que pasó por varias vías las lista TODAS, en el orden en que
        // las recorrió. Enseñar solo la de salida diría que el tramo fue por una
        // carretera en la que solo estuvo un trozo.
        road: showRoad
          ? (segment.roadStretches && segment.roadStretches.length > 0
              ? segment.roadStretches
                  .map((st) => roadNamesById?.[st.roadId] ?? '')
                  .filter(Boolean)
                  .join(' · ')
              : roadNamesById?.[segment.roadId ?? firstRoadId ?? '']) ||
            EMPTY_CELL
          : undefined,
        direction: stripLeadingWord(directionLabel, directionHeaderWord),
        lane: segment.lane ? laneName(segment.lane) : EMPTY_CELL,
        pkInit: formatPK(segment.pkInitKm ?? 0, segment.pkInitMeter ?? 0),
        pkEnd: hasPkEnd
          ? formatPK(segment.pkEndKm as number, segment.pkEndMeter as number)
          : EMPTY_CELL,
        km: formatSegmentKm(
          segment.pkInitKm,
          segment.pkInitMeter,
          segment.pkEndKm,
          segment.pkEndMeter,
          segment.roadStretches,
          {
            roadId: segment.roadId ?? firstRoadId,
            meshesByRoadId,
          },
        ),
        startTime: segment.startDate
          ? format(new Date(segment.startDate), 'HH:mm')
          : EMPTY_CELL,
        endTime: closed
          ? format(new Date(segment.endDate as string), 'HH:mm')
          : EMPTY_CELL,
        duration: formatSegmentDuration(segment.startDate, segment.endDate),
        pauseCause: segment.pauseCause
          ? t(
              `pages:surveillance.pause.causes.${segment.pauseCause}`,
              segment.pauseCause,
            )
          : EMPTY_CELL,
        description: segment.pauseCauseDetail ?? '',
        // Una básica anulada no resuelve el tramo: no figura en el informe.
        // 30-oct: sin básica, la columna lleva la actuación o el accidente que
        // atendió el aviso, uno por línea y marcado «De la OT · X» para que no
        // se confunda con una básica (como la tabla de pantalla). Bajo la OT, en
        // «OT sala», la fila crecía al doble.
        linkedBasicActionRoid:
          liveLinkedBasicActionOrder(segment.linkedBasicActionOrder)?.roid ??
          (avisoWorkRoids.length > 0
            ? avisoWorkRoids
                .map((roid) =>
                  t('pages:surveillance.fromOtWork', undefined, { roid }),
                )
                .join('\n')
            : EMPTY_CELL),
        linkedExternalRoid: segment.linkedExternalOrder?.roid || EMPTY_CELL,
        linkedBasicActionOrderId:
          liveLinkedBasicActionOrder(segment.linkedBasicActionOrder)?.id ?? '',
        causeKey: segment.pauseCause ?? null,
        durationMs:
          closed && segment.startDate
            ? Math.max(
                0,
                new Date(segment.endDate as string).getTime() -
                  new Date(segment.startDate).getTime(),
              )
            : 0,
        // Igual que la columna Km: con trozos, la SUMA de lo hecho en cada vía.
        // Este valor alimenta el agregado por causa de pausa del informe, así
        // que restar los extremos ahí falsearía los kilómetros del resumen.
        meters: hasPkEnd
          ? (segmentMeters(segment, meshesByRoadId) ?? 0)
          : 0,
      };
    });
};

/** Duración total (ms) de las filas — el "Duración total tramos" de la ficha. */
export const sumSegmentDurationMs = (
  rows: SurveillanceSegmentRow[],
): number => rows.reduce((total, row) => total + row.durationMs, 0);

/**
 * Agregado por causa de pausa. Los tramos abiertos aportan 0 (mismo criterio
 * que la pantalla: no se inventa un elapsed en una tabla estática) y los que
 * no tienen causa —cerrados al finalizar el recorrido— no forman grupo.
 */
export const buildCauseSummary = (
  rows: SurveillanceSegmentRow[],
  t: ReportTranslate,
): SurveillanceCauseRow[] => {
  const acc = new Map<string, { count: number; ms: number; meters: number }>();

  rows.forEach((row) => {
    if (!row.causeKey) return;
    const current = acc.get(row.causeKey) ?? { count: 0, ms: 0, meters: 0 };
    acc.set(row.causeKey, {
      count: current.count + 1,
      ms: current.ms + row.durationMs,
      meters: current.meters + row.meters,
    });
  });

  return [...acc.entries()].map(([causeKey, value]) => ({
    causeKey,
    cause: t(`pages:surveillance.pause.causes.${causeKey}`, causeKey),
    count: value.count,
    // Reusa el MISMO formateador que la columna Duración de la tabla (`Xh Ym`)
    // pasándole una ventana equivalente desde epoch.
    duration: formatSegmentDuration(
      new Date(0).toISOString(),
      new Date(value.ms).toISOString(),
    ),
    km: (value.meters / 1000).toFixed(1),
  }));
};
